import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Shield, Clock, Star } from 'lucide-react';

const Pricing = () => {
  return (
    <div className="pt-16">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-primary-50 via-white to-accent-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block mb-6 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-primary-200">
            <span className="text-primary-600 font-semibold text-sm">✨ Bespoke Pricing</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-display font-bold mb-6 leading-tight">
            <span className="text-gradient">A Quote Built Around</span>
            <br />
            <span className="text-gray-900">Your Business</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed mb-10">
            Every Voice AI agent we build is bespoke, so your pricing is too. We base your quote on your specific requirements — the size of your business, your call volume, and what you need the agent to do. Book a free discovery call to get your personalised quote.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center bg-gradient-to-r from-primary-600 to-accent-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
          >
            Get Your Free Quote
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>


      {/* How Minutes Work */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-primary-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-display font-bold text-center mb-10 text-gray-900">How It All Works</h2>
          <div className="space-y-6">
            {[
              {
                icon: Clock,
                title: 'How your quote is built',
                body: 'Every quote is tailored to your business. In your discovery call we\'ll look at factors including the size of your team, your expected monthly call volume, the complexity of your call flows, and any integrations required. From there we put together a fixed monthly retainer with a clear, included minutes allowance — so you always know exactly what you\'re paying.'
              },
              {
                icon: Shield,
                title: 'Satisfaction guarantee',
                body: 'We will never keep your money if our product does not work as promised. If the agent fails to meet the agreed brief due to a technical, script, or integration failure, your setup fee is refunded within 14 business days.'
              },
              {
                icon: Star,
                title: 'Upfront discount',
                body: 'Pay 6 months in advance and receive 1 month free.'
              }
            ].map(({ icon: Icon, title, body }, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex gap-5">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">{title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 2 — Client Guarantee */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4 text-gray-900">
              Client Guarantee &amp; Protection Policy
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Both sides are guaranteed fair treatment. Clients are protected against paying for a product that does not perform. MMI Online is protected against investing significant build time without genuine commitment.
            </p>
          </div>

          <div className="rounded-2xl border-2 border-gray-200 p-8 bg-gray-50 mb-12">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Satisfaction Guarantee</h3>
              <p className="text-gray-700 text-sm mb-4">
                See the completed, tested agent before fully committing. Pay the full standard setup fee once satisfied.
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /> Full standard setup fee applies</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /> See the working agent before go-live</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" /> Full satisfaction guarantee in place</li>
              </ul>
              <p className="text-xs text-gray-500 mt-4">
                <em>A minimum 30-day live period applies after go-live before any refund can be exercised. Refund right applies only where the agent fails to meet agreed KPIs during that period.</em>
              </p>
            </div>

          {/* How the process works */}
          <div className="bg-gradient-to-br from-primary-600 to-accent-600 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-6 text-center">How the Process Works</h3>
            <ol className="space-y-4">
              {[
                ['Brief agreed', 'Before any payment is taken, MMI Online and the client agree a written brief covering call flows, responses, integrations required, and success criteria.'],
                ['Payment taken', 'The setup fee is taken before build begins.'],
                ['Build & testing', 'MMI Online builds and internally tests the agent against the agreed brief. Typically 5–10 business days.'],
                ['Client review', 'You review the first draft of the agent\'s prompt and call flow documents. Revisions are incorporated and sign-off is required before the build proceeds.'],
                ['Go-live', 'On client approval, the agent goes live. The monthly retainer begins from this date.'],
                ['Guarantee period', '30-day guarantee from go-live. Refund right applies where the agent fails to meet agreed KPIs during that period.'],
              ].map(([step, desc], i) => (
                <li key={i} className="flex gap-4">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">{i + 1}</span>
                  <div>
                    <span className="font-semibold">{step}: </span>
                    <span className="text-white/85 text-sm">{desc}</span>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-primary-50">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-display font-bold mb-4 text-gray-900">Ready to get started?</h2>
          <p className="text-gray-600 mb-8">Book a free discovery call and we'll help you find the right plan for your business.</p>
          <Link
            to="/contact"
            className="inline-flex items-center bg-gradient-to-r from-primary-600 to-accent-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
          >
            Book a Free Discovery Call
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
          <p className="mt-6 text-sm text-gray-500">
            Want full details on what's included?{' '}
            <Link to="/pricing-policy" className="text-primary-600 hover:text-primary-700 font-semibold underline">
              View our Pricing Policy
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
